import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faUser, faEnvelope, faShieldAlt, faCalendar, faArrowLeft, faSave, faTimes, faHome } from '@fortawesome/free-solid-svg-icons';
import type { User as UserType, CreateUserData, UpdateUserData } from '../types';
import './AdminPage.css';

export const AdminPage: React.FC = () => {
  const { user, getAllUsers, createUser, updateUser, deleteUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState<CreateUserData>({
    email: '',
    name: '',
    password: '',
    role: 'user'
  });
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const usersList = await getAllUsers();
      setUsers(usersList);
    } catch (error) {
      setMessage('Erro ao carregar usuários');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    const result = await createUser(formData);
    if (result.success) {
      setMessage('Usuário criado com sucesso!');
      setFormData({ email: '', name: '', password: '', role: 'user' });
      setShowCreateForm(false);
      loadUsers();
    } else {
      setMessage(result.error || 'Erro ao criar usuário');
    }
    setIsLoading(false);
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    setIsLoading(true);
    setMessage('');

    const updateData: UpdateUserData = {
      id: editingUser.id,
      email: formData.email,
      name: formData.name,
      role: formData.role
    };

    const result = await updateUser(updateData);
    if (result.success) {
      setMessage('Usuário atualizado com sucesso!');
      setEditingUser(null);
      setFormData({ email: '', name: '', password: '', role: 'user' });
      loadUsers();
    } else {
      setMessage(result.error || 'Erro ao atualizar usuário');
    }
    setIsLoading(false);
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Tem certeza que deseja excluir este usuário?')) return;

    setIsLoading(true);
    setMessage('');

    const result = await deleteUser(userId);
    if (result.success) {
      setMessage('Usuário excluído com sucesso!');
      loadUsers();
    } else {
      setMessage(result.error || 'Erro ao excluir usuário');
    }
    setIsLoading(false);
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    setIsLoading(true);
    setMessage('');

    const result = await updateUser({
      id: userId,
      isActive: !currentStatus
    });

    if (result.success) {
      setMessage(`Usuário ${!currentStatus ? 'ativado' : 'desativado'} com sucesso!`);
      loadUsers();
    } else {
      setMessage(result.error || 'Erro ao alterar status do usuário');
    }
    setIsLoading(false);
  };

  const startEdit = (userToEdit: UserType) => {
    setEditingUser(userToEdit);
    setFormData({
      email: userToEdit.email,
      name: userToEdit.name,
      password: '',
      role: userToEdit.role as 'admin' | 'user'
    });
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setFormData({ email: '', name: '', password: '', role: 'user' });
  };

  const getRoleDisplay = (role: string) => {
    const roles = {
      master: 'Master',
      admin: 'Administrador',
      user: 'Usuário'
    };
    return roles[role as keyof typeof roles] || role;
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    navigate(-1);
  };


  if (!user || !user.role || (user.role !== 'admin' && user.role !== 'master')) {
    return (
      <div className="app-container">
        <Header />
        <div className="admin-container">
          <div className="admin-error">
            <h2>Acesso Negado</h2>
            <p>Você não tem permissão para acessar esta página.</p>
            <div className="admin-error-actions">
              <button onClick={handleGoHome} className="admin-btn-home">
                <FontAwesomeIcon icon={faHome} />
                Voltar ao Início
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app-container">
      <Header />
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-top">
            <div className="admin-header-actions">
              <button 
                onClick={handleGoBack}
                className="admin-btn-back"
                title="Voltar para página anterior"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                Voltar
              </button>
              <button 
                onClick={handleGoHome}
                className="admin-btn-home"
                title="Voltar ao início"
              >
                <FontAwesomeIcon icon={faHome} />
                Início
              </button>
            </div>
          </div>
          <div className="admin-header-content">
            <h1 className="admin-title">Central de Administração</h1>
            <p className="admin-subtitle">Gerencie usuários e configurações do sistema</p>
          </div>
          <div className="admin-header-tools">
            <button
              onClick={() => setShowInactiveUsers(!showInactiveUsers)}
              className={`admin-filter-button ${showInactiveUsers ? 'active' : ''}`}
              title={showInactiveUsers ? 'Mostrar apenas usuários ativos' : 'Mostrar usuários inativos'}
            >
              {showInactiveUsers ? 'Ativos' : 'Inativos'}
            </button>
            <button
              onClick={() => setShowCreateForm(true)}
              className="admin-create-button"
              title="Criar novo usuário"
            >
              <FontAwesomeIcon icon={faPlus} />
              Novo Usuário
            </button>
          </div>
        </div>

      {message && (
        <div className={`admin-message ${message.includes('sucesso') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {showCreateForm && (
        <div className="admin-form-overlay">
          <div className="admin-form">
            <div className="admin-form-header">
              <h3>Criar Novo Usuário</h3>
              <button 
                onClick={() => setShowCreateForm(false)} 
                className="admin-form-close"
                title="Fechar formulário"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="admin-field">
                <label htmlFor="create-email" className="admin-label">
                  Email
                </label>
                <input
                  type="email"
                  id="create-email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="admin-input"
                  required
                />
              </div>

              <div className="admin-field">
                <label htmlFor="create-name" className="admin-label">
                  Nome
                </label>
                <input
                  type="text"
                  id="create-name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="admin-input"
                  required
                />
              </div>

              <div className="admin-field">
                <label htmlFor="create-password" className="admin-label">
                  Senha
                </label>
                <input
                  type="password"
                  id="create-password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="admin-input"
                  required
                />
              </div>

              <div className="admin-field">
                <label htmlFor="create-role" className="admin-label">
                  Perfil
                </label>
                <select
                  id="create-role"
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as 'admin' | 'user' }))}
                  className="admin-select"
                >
                  <option value="user">Usuário</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>

              <div className="admin-form-actions">
                <button type="button" onClick={() => setShowCreateForm(false)} className="admin-cancel-button">
                  Cancelar
                </button>
                <button type="submit" className="admin-save-button" disabled={isLoading}>
                  <FontAwesomeIcon icon={faSave} />
                  {isLoading ? 'Criando...' : 'Criar Usuário'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingUser && (
        <div className="admin-form-overlay">
          <div className="admin-form">
            <div className="admin-form-header">
              <h3>Editar Usuário</h3>
              <button 
                onClick={cancelEdit} 
                className="admin-form-close"
                title="Fechar formulário"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleUpdateUser}>
              <div className="admin-field">
                <label htmlFor="edit-email" className="admin-label">
                  Email
                </label>
                <input
                  type="email"
                  id="edit-email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="admin-input"
                  required
                />
              </div>

              <div className="admin-field">
                <label htmlFor="edit-name" className="admin-label">
                  Nome
                </label>
                <input
                  type="text"
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="admin-input"
                  required
                />
              </div>

              <div className="admin-field">
                <label htmlFor="edit-role" className="admin-label">
                  Perfil
                </label>
                <select
                  id="edit-role"
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as 'admin' | 'user' }))}
                  className="admin-select"
                >
                  <option value="user">Usuário</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>

              <div className="admin-form-actions">
                <button type="button" onClick={cancelEdit} className="admin-cancel-button">
                  Cancelar
                </button>
                <button type="submit" className="admin-save-button" disabled={isLoading}>
                  <FontAwesomeIcon icon={faSave} />
                  {isLoading ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admin-content">
        {isLoading ? (
          <div className="admin-loading">Carregando usuários...</div>
        ) : (
          <div className="admin-users-grid">
            {users
              .filter(userItem => showInactiveUsers ? !userItem.isActive : userItem.isActive)
              .map((userItem) => (
              <div key={userItem.id} className={`admin-user-card ${!userItem.isActive ? 'inactive' : ''}`}>
                <div className="admin-user-avatar">
                  <FontAwesomeIcon icon={faUser} />
                </div>
                <div className="admin-user-info">
                  <h3 className="admin-user-name">
                    {userItem.name}
                    {!userItem.isActive && <span className="admin-user-status-badge">Inativo</span>}
                  </h3>
                  <div className="admin-user-email">
                    <FontAwesomeIcon icon={faEnvelope} />
                    <span>{userItem.email}</span>
                  </div>
                  <div 
                    className={`admin-user-role admin-user-role-${userItem.role}`}
                  >
                    <FontAwesomeIcon icon={faShieldAlt} />
                    <span>{getRoleDisplay(userItem.role)}</span>
                  </div>
                  <div className="admin-user-date">
                    <FontAwesomeIcon icon={faCalendar} />
                    <span>Criado em {new Date(userItem.createdAt).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
                <div className="admin-user-actions">
                  <button
                    onClick={() => handleToggleUserStatus(userItem.id, userItem.isActive)}
                    className={`admin-toggle-button ${userItem.isActive ? 'deactivate' : 'activate'}`}
                    disabled={userItem.role === 'master'}
                    title={userItem.isActive ? 'Desativar usuário' : 'Ativar usuário'}
                  >
                    {userItem.isActive ? 'Desativar' : 'Ativar'}
                  </button>
                  <button
                    onClick={() => startEdit(userItem)}
                    className="admin-edit-button"
                    disabled={userItem.role === 'master'}
                    title="Editar usuário"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    onClick={() => handleDeleteUser(userItem.id)}
                    className="admin-delete-button"
                    disabled={userItem.role === 'master' || userItem.id === user?.id}
                    title="Excluir usuário"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
            {users.filter(userItem => showInactiveUsers ? !userItem.isActive : userItem.isActive).length === 0 && (
              <div className="admin-empty-state">
                <p>{showInactiveUsers ? 'Nenhum usuário inativo encontrado' : 'Nenhum usuário ativo encontrado'}</p>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
      <Footer />
    </div>
  );
};
